import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { useOrgContext } from "../../context/OrgContext";
import { PickerButton } from "./PickerButton";

/**
 * Slim header — sidebar owns the brand and venue switching. The topbar
 * carries the global organisation switcher (left) and the user menu + ⌘K
 * hint (right). FPZERO admins see "Operations Overview" as a top item in
 * the org dropdown.
 */
export function Topbar() {
  const { user, logout } = useAuth();
  const { orgs, currentOrg } = useOrgContext();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <header className="bg-white border-b border-gray-200 px-4 h-12 flex items-center justify-between gap-3 shrink-0">
      <PickerButton
        value={currentOrg?.name ?? (user?.isFpzeroAdmin ? "Select organisation" : "Choose organisation")}
        items={[
          ...(user?.isFpzeroAdmin
            ? [{ id: "__overview__", label: "Operations Overview", to: "/", accent: true }]
            : []),
          ...orgs.map((o) => ({
            id: o.id,
            label: o.name,
            to: `/org/${o.slug}`,
            active: currentOrg?.id === o.id,
          })),
          ...(orgs.length === 0
            ? [{ id: "__none__", label: "No organisations", disabled: true }]
            : []),
        ]}
        onPick={(item) => item.to && navigate(item.to)}
      />

      <div className="flex items-center gap-3">
        <span
          className="hidden md:inline-flex items-center gap-1 text-xs text-gray-400 px-2 py-1 rounded border border-gray-200"
          title="Press Cmd-K (Ctrl-K on Windows) to open the command palette."
        >
          <kbd className="font-sans">⌘K</kbd>
        </span>

        <Menu
          open={userMenuOpen}
          onOpenChange={setUserMenuOpen}
          trigger={
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white text-xs font-semibold">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </span>
              <span className="hidden sm:inline">{user?.name || user?.email}</span>
              <Chevron />
            </button>
          }
        >
          <div className="px-4 py-2 border-b border-gray-100">
            <div className="text-sm font-medium text-gray-900 truncate">{user?.name}</div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            {user?.isFpzeroAdmin && (
              <div className="mt-1 inline-flex rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-purple-700">
                FPZERO admin
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setUserMenuOpen(false);
              logout();
            }}
            className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            Sign out
          </button>
        </Menu>
      </div>
    </header>
  );
}

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" className="text-gray-400">
      <path d="M2 4.5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Menu({
  open,
  onOpenChange,
  trigger,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onOpenChange(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div ref={ref} className="relative">
      {trigger}
      {open && (
        <div className="absolute right-0 z-40 mt-1 min-w-[12rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {children}
        </div>
      )}
    </div>
  );
}
